/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Typography, Stack, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';

import { CustomFilterV2, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { OLERITE } from '../../../constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import * as actionsOlerite from '../../../store/olerite/actions';

import Formulario from './Formulario';
import FormularioEdicao from './FormularioEdicao';
import { getTipoOleriteLabel, tipoOleriteOptions } from './constants';

const hasPermission = (moduleConfigurations, authData) =>
  hasModuleConfiguration(moduleConfigurations, authData, OLERITE);

const defaultFilter = (moduleConfigurations, authData) => ({
  usuarioId: !hasPermission(moduleConfigurations, authData)
    ? authData?.id
      ? [authData.id]
      : []
    : [],
});

const OleriteV2 = ({ authData, moduleConfigurations, deleteOlerite }) => {
  const adminPermission = hasPermission(moduleConfigurations, authData);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdicao, setOpenEdicao] = useState(false);
  const [id, setId] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const [localFilter, setLocalFilter] = useState(() =>
    defaultFilter(moduleConfigurations, authData));

  const defaultFilterMemo = useMemo(
    () => defaultFilter(moduleConfigurations, authData),
    [moduleConfigurations, authData]
  );

  const apiFilter = useMemo(
    () =>
      adminPermission
        ? { ...localFilter }
        : {
          ...localFilter,
          usuarioId: authData?.id ? [authData.id] : [],
        },
    [adminPermission, localFilter, authData]
  );

  const onEdit = (rowId) => {
    setId(rowId);
    setOpenEdicao(true);
  };

  const onAdd = () => {
    setId(null);
    setOpenAdd(true);
  };

  const onClose = (save) => {
    setId(null);
    setOpenEdicao(false);
    setOpenAdd(false);
    if (save) {
      setReload((r) => !r);
    }
  };

  const onDelete = (rowId) =>
    deleteOlerite(rowId, () => setReload((r) => !r));

  const headerActions = [];
  if (adminPermission) {
    headerActions.push({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Holerites</Typography>

          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="olerites/filter"
          rowHeight={70}
          headerActions={headerActions}
          columns={[
            {
              field: 'mesReferenciaValue',
              headerName: 'Mês/Ano Referência',
              flex: 0.85,
              minWidth: 160,
            },
            {
              field: 'tipo',
              headerName: 'Tipo',
              width: 200,
              renderCell: ({ row }) => getTipoOleriteLabel(row.tipo),
            },
            { field: 'usuarioNome', headerName: 'Usuário', flex: 0.85, minWidth: 160 },
            { field: 'criadoPorNome', headerName: 'Criado Por', flex: 0.7, minWidth: 140 },
            {
              field: 'dataInclusaoValue',
              headerName: 'Data Inclusão',
              flex: 0.6,
              minWidth: 140,
            },
          ]}
          onEdit={adminPermission ? onEdit : undefined}
          onView={!adminPermission ? onEdit : undefined}
          onDelete={adminPermission ? onDelete : undefined}
          canDelete={() => adminPermission}
          filterData={apiFilter}
          refreshKey={reload}
          pageSize={10}
          autoPageSize={false}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilterMemo}
          appliedFilter={localFilter}
          fields={[
            { field: 'useMesReferencia', md: 6 },
            {
              field: 'useSelectOption',
              md: 6,
              key: 'tipo',
              label: 'Tipo',
              options: tipoOleriteOptions,
            },
            {
              field: 'useUsuarios',
              md: 12,
              xs: 12,
              multiSelect: true,
              disabled: !adminPermission,
            },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario open={openAdd} onClose={onClose} />
        <FormularioEdicao
          open={openEdicao}
          onClose={onClose}
          adminPermission={adminPermission}
          id={id}
        />
      </Stack>
    </Box>
  );
};

OleriteV2.propTypes = {
  authData: PropTypes.object,
  moduleConfigurations: PropTypes.array,
  deleteOlerite: PropTypes.func.isRequired,
};

OleriteV2.defaultProps = {
  authData: {},
  moduleConfigurations: [],
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations:
    selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsOlerite }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(OleriteV2);
